/**
 * "Rebuild Analytics" -- POST /api/hydrate/{id}, then poll GET /api/hydrate/status/{id}
 * until THAT job finishes. One implementation for both Data pages.
 *
 * The old loop polled "while needs_hydration" forever: an upload whose rebuild failed
 * stays pending, so the button would spin for good. This waits on the job instead and
 * hands back its result, errors included -- the caller shows them (LoadError), never a
 * silent "done".
 *
 * Plain JS on purpose (no JSX), like loadState.js.
 */
import { hydrate, getHydrationStatus } from '../api'

const POLL_MS = 3000
const MAX_POLLS = 1200 // an hour

// Resolves to { status, job, message } once the rebuild job has finished (job is null when
// there was nothing to rebuild). THROWS when the rebuild could not start (a sync is running)
// or did not finish cleanly -- the error's message is what to show.
export async function rebuildAnalytics(customerId, onStatus) {
  const start = await hydrate(customerId)
  if (start?.status === 'nothing_to_do') {
    const status = await getHydrationStatus(customerId)
    onStatus?.(status)
    return { status, job: null, message: start.message }
  }
  if (start?.status === 'already_running' && start.kind !== 'hydration') {
    throw new Error(start.message || 'A sync is running for this customer. Try again when it finishes.')
  }
  const jobId = start?.job_id
  if (!jobId) throw new Error(`Unexpected response from the rebuild: ${JSON.stringify(start)}`)
  for (let i = 0; i < MAX_POLLS; i++) {
    await new Promise(r => setTimeout(r, POLL_MS))
    const status = await getHydrationStatus(customerId)
    onStatus?.(status)
    const job = status?.job
    if (job && job.job_id === jobId && job.status !== 'running') {
      if (job.status !== 'completed' || (job.errors || []).length) {
        const restored = job.tables_restored || 0
        throw new Error(`Rebuilt ${restored} of ${job.tables_total || 0} table(s). `
          + ((job.errors || []).slice(0, 3).join('; ') || job.status))
      }
      return { status, job, message: `Rebuilt ${job.tables_restored} table(s), ${Number(job.total_rows || 0).toLocaleString()} rows.` }
    }
  }
  throw new Error(`The rebuild is still running (job ${jobId}). Check back later.`)
}
